import { useNativePreferences } from '../storage/preferences';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, findNodeHandle, FlatList, Platform, StyleSheet, Text, TextInput, View } from 'react-native';
import { ChevronDown, Clapperboard, Film, House, Monitor, Music2, Radio, Search, Tv, UserRound } from 'lucide-react-native';
import { NavigationProp } from '@react-navigation/native';
import { RootStackParamList } from '../navigation/types';
import { colors, getBrowseVisualTokens, spacing, systemSansFont, typography } from '../theme/tokens';
import { FocusablePressable } from './FocusablePressable';
import { useSession } from '../session/SessionProvider';
import Svg, { Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useReferenceWindowDimensions } from '../utils/referenceViewport';
import { isTelevisionRuntime } from '../utils/platformCapabilities';
import { RemotePlaybackControl } from './RemotePlaybackControl';
import { useAppShell } from './AppShellContext';
import { Broadcast, FilmSlate, House as PhosphorHouse, MagnifyingGlass, MusicNote, TelevisionSimple } from 'phosphor-react-native';
import { scrollWebContentToTop } from '../tvNavigation/registry';

type Props = {
    navigation: NavigationProp<RootStackParamList>;
    active: keyof RootStackParamList;
    activeItemType?: 'Movie' | 'Series';
    overlay?: boolean;
    solid?: boolean;
    persistentSearch?: boolean;
    searchValue?: string;
    onSearchValueChange?: (value: string) => void;
    onSearchInputReady?: (node: number | null) => void;
    sideRail?: boolean;
    expanded?: boolean;
    hideBrand?: boolean;
};
const links = [
    { label: 'Home', route: 'Home' as const, icon: House },
    { label: 'TV Shows', route: 'Library' as const, params: { itemType: 'Series' as const }, icon: Monitor },
    { label: 'Movies', route: 'Library' as const, params: { itemType: 'Movie' as const }, icon: Film },
    { label: 'Music', route: 'Music' as const, icon: Music2 },
    { label: 'Live TV', route: 'LiveTV' as const, icon: Radio },
];
const phosphorRailIcons: Record<string, React.ComponentType<any>> = { Home: PhosphorHouse, 'TV Shows': TelevisionSimple, Movies: FilmSlate, Music: MusicNote, 'Live TV': Broadcast };

function MatureSearchIcon({ size }: { size: number }) {
    return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
            <Path
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                fill="none"
                stroke="#fff"
                strokeWidth={2}
                strokeLinecap="round"
            />
        </Svg>
    );
}

export const BOTTOM_NAV_BASE_HEIGHT = 68;

/**
 * Height screens should add as bottom content padding so the fixed bottom
 * navigation bar never covers scrollable content. Returns 0 when the bottom
 * bar is not active (desktop widths or the setting disabled).
 */
export function useBottomNavInset(): number {
    const { width } = useReferenceWindowDimensions();
    const insets = useSafeAreaInsets();
    const prefs = useNativePreferences();
    return width <= 760 && prefs.bottomNavMobile ? BOTTOM_NAV_BASE_HEIGHT + insets.bottom : 0;
}

export function MatureTopNav({ navigation, active, activeItemType, overlay = false, solid = false, persistentSearch = false, searchValue, onSearchValueChange, onSearchInputReady, sideRail = false, expanded = false, hideBrand = false }: Props) {
    const appShell = useAppShell();
    const { width } = useReferenceWindowDimensions();
    const browseVisuals = getBrowseVisualTokens(width);
    const insets = useSafeAreaInsets();
    const compact = width <= 760;
    const [searchOpen, setSearchOpen] = useState(false);
    const [localQuery, setLocalQuery] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [remoteOpen, setRemoteOpen] = useState(false);
    const prefs = useNativePreferences();
    const bottomNav = compact && prefs.bottomNavMobile;
    const searchProgress = useRef(new Animated.Value(persistentSearch ? 1 : 0)).current;
    const searchInput = useRef<TextInput | null>(null);
    const { user, signOut } = useSession();
    const sidePadding = useMemo(
        () => browseVisuals.viewport === 'television' ? browseVisuals.pagePadding : width >= 1280 ? 64 : compact ? 16 : 40,
        [browseVisuals.pagePadding, browseVisuals.viewport, compact, width],
    );
    const query = searchValue ?? localQuery;
    const setQuery = onSearchValueChange ?? setLocalQuery;
    const resolvedSearchOpen = persistentSearch || searchOpen;
    const searchWidth = persistentSearch
        ? Math.min(520, Math.max(340, Math.round(width * 0.28)))
        : Math.min(290, Math.round(width * 0.34));
    const searchEnabled = active !== 'Search' || persistentSearch;
    const replaceRoute = useCallback((name: keyof RootStackParamList, params?: unknown) => {
        (navigation as NavigationProp<RootStackParamList> & { replace: (routeName: keyof RootStackParamList, routeParams?: unknown) => void }).replace(name, params);
    }, [navigation]);
    const submit = () => {
        const value = query.trim();
        if (value) {
            if (persistentSearch) searchInput.current?.blur();
            else {
                setSearchOpen(false);
                replaceRoute('Search', { query: value });
            }
        }
    };
    const isCurrentLink = (item: (typeof links)[number]) => item.route === 'Library'
        ? active === 'Library' && item.params.itemType === activeItemType
        : active === item.route;
    const openLink = (item: (typeof links)[number]) => {
        if (isCurrentLink(item)) {
            scrollWebContentToTop();
            return;
        }
        if (item.route === 'Library') {
            replaceRoute('Library', item.params);
        }
        else if (item.route === 'Home') replaceRoute('Home');
        else navigation.navigate(item.route);
    };

    useEffect(() => {
        Animated.timing(searchProgress, {
            toValue: resolvedSearchOpen ? 1 : 0,
            duration: 300,
            useNativeDriver: false,
        }).start();
        if (!resolvedSearchOpen) return undefined;
        if (persistentSearch && isTelevisionRuntime()) return undefined;
        const focusTimer = setTimeout(() => searchInput.current?.focus(), 80);
        return () => clearTimeout(focusTimer);
    }, [resolvedSearchOpen, searchProgress]);

    useEffect(() => {
        if (bottomNav || persistentSearch || !resolvedSearchOpen || !query.trim()) return undefined;
        const searchTimer = setTimeout(() => {
            navigation.reset({ index: 0, routes: [{ name: 'Search', params: { query: query.trim() } }] });
        }, 300);
        return () => clearTimeout(searchTimer);
    }, [bottomNav, persistentSearch, query, replaceRoute, resolvedSearchOpen]);

    const handleSearchPress = () => {
        if (persistentSearch) searchInput.current?.focus();
        else if (searchOpen) submit();
        else setSearchOpen(true);
    };

    const handleSearchKey = (key: string) => {
        if (key === 'Escape') {
            if (persistentSearch) searchInput.current?.blur();
            else setSearchOpen(false);
        }
    };

    useEffect(() => {
        if (persistentSearch || active === 'Search') return;
        setSearchOpen(false);
        setLocalQuery('');
    }, [active, persistentSearch]);

    const setSearchInputRef = useCallback((node: TextInput | null) => {
        searchInput.current = node;
        // The numeric handle is consumed only by native-TV focus. Avoid
        // React Native Web's deprecated findDOMNode path on the web.
        onSearchInputReady?.(Platform.OS === 'web' || !node ? null : findNodeHandle(node));
    }, [onSearchInputReady]);

    const linkList = (
        <FlatList
            horizontal={!sideRail}
            style={!compact ? [styles.desktopLinks, sideRail && styles.sideRailLinks] : undefined}
            data={links}
            keyExtractor={(item) => item.label}
            showsHorizontalScrollIndicator={false}
            removeClippedSubviews={false}
            contentContainerStyle={compact ? styles.mobileLinksContent : styles.desktopLinksContent}
            renderItem={({ item }) => {
                const selected = item.route === 'Library'
                    ? active === 'Library' && item.params.itemType === activeItemType
                    : active === item.route;
                return (
                    <FocusablePressable onPress={() => openLink(item)} style={(focused, pressed, hovered) => [styles.linkButton, sideRail && styles.sideLinkButton, compact && styles.linkButtonCompact, (hovered || (!sideRail && focused)) && styles.navFocused, pressed && styles.controlPressed]}>
                        {(focused, hovered) => <>
                            {sideRail ? (() => { const RailIcon = phosphorRailIcons[item.label]; return <RailIcon color={(focused || hovered || selected) ? colors.text : colors.textMuted} size={23} weight={selected ? 'bold' : 'regular'} />; })() : null}
                            <Text numberOfLines={1} style={[styles.linkText, { fontSize: browseVisuals.navFontSize, lineHeight: browseVisuals.navLineHeight }, compact && styles.linkTextCompact, sideRail && !expanded && styles.sideRailLinkText, sideRail && expanded && styles.sideRailExpandedLinkText, (focused || hovered) && styles.linkTextHovered, selected && styles.linkTextActive]}>{item.label}</Text>
                            {sideRail && hovered ? <View pointerEvents="none" style={styles.sideHoverLabel}><Text numberOfLines={1} style={styles.sideHoverLabelText}>{item.label}</Text></View> : null}
                            {selected ? <View style={[styles.activeBar, compact && styles.activeBarCompact, sideRail && styles.sideActiveBar]} /> : null}
                        </>}
                    </FocusablePressable>
                );
            }}
        />
    );

    const railLinkList = <View style={styles.railLinkList}>{links.map((item, index) => {
        const selected = item.route === 'Library'
            ? active === 'Library' && item.params.itemType === activeItemType
            : active === item.route;
        const focusId = `shell.rail.${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
        const previousId = index > 0 ? `shell.rail.${links[index - 1].label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : undefined;
        const nextId = index < links.length - 1 ? `shell.rail.${links[index + 1].label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : 'shell.utility.profile';
        return <FocusablePressable key={item.label} testID="tv-rail-control" focusId={focusId} focusGroup="shell.rail" focusRole="rail" focusDestinations={{ ...(previousId ? { up: previousId } : {}), down: nextId }} onPress={() => openLink(item)} style={(focused, pressed, hovered) => [styles.linkButton, styles.sideLinkButton, (hovered || (!sideRail && focused)) && styles.navFocused, pressed && styles.controlPressed]}>
            {(focused, hovered) => { const RailIcon = phosphorRailIcons[item.label]; return <><RailIcon color={(focused || hovered || selected) ? colors.text : colors.textMuted} size={23} weight={selected ? 'bold' : 'regular'} />{hovered ? <View pointerEvents="none" style={styles.sideHoverLabel}><Text numberOfLines={1} style={styles.sideHoverLabelText}>{item.label}</Text></View> : null}{selected ? <View style={[styles.activeBar, styles.sideActiveBar]} /> : null}</>; }}
        </FocusablePressable>;
    })}</View>;

    const topBar = (
        <View pointerEvents={overlay ? "box-none" : "auto"} style={[styles.root, !compact && styles.rootDesktop, sideRail && styles.rootSideRail, browseVisuals.viewport === 'television' && styles.rootTelevision, overlay && styles.overlay, solid && styles.solid, { paddingHorizontal: sideRail ? 12 : sidePadding }, overlay && insets.top > 0 && { paddingTop: (compact ? spacing.sm : 10) + insets.top }]}>
            {overlay && !solid ? <Svg pointerEvents="none" width={width} height={compact ? 126 : 62} style={styles.navGradient}>
                <Defs><LinearGradient id="navFade" x1="0" y1="0" x2="0" y2="1"><Stop offset="0" stopColor="#000" stopOpacity="0.64" /><Stop offset="0.52" stopColor="#000" stopOpacity="0.24" /><Stop offset="1" stopColor="#000" stopOpacity="0" /></LinearGradient></Defs>
                <Rect x="0" y="0" width={width} height={compact ? 126 : 62} fill="url(#navFade)" />
            </Svg> : null}
            <View pointerEvents="box-none" style={[styles.mainRow, !compact && styles.mainRowDesktop, sideRail && styles.sideMainRow]}>
                {!hideBrand ? <FocusablePressable controllerFocusable={false} onPress={() => openLink(links[0])} style={(focused, pressed, hovered) => [styles.brandButton, compact && styles.brandButtonCompact, sideRail && styles.sideBrandButton, (focused || hovered) && styles.navFocused, pressed && styles.controlPressed]}><Text numberOfLines={1} style={[typography.brand, !compact && styles.brandDesktop, sideRail && styles.sideBrand, browseVisuals.viewport === 'television' && styles.brandTelevision]}>JELLYFLIX</Text></FocusablePressable> : null}
                {!compact && sideRail ? <View style={styles.sidePrimaryGroup}>
                    <FocusablePressable testID="tv-rail-control" focusId="shell.rail.search" focusGroup="shell.rail" focusRole="rail" accessibilityLabel="Open search" onPress={handleSearchPress} style={(focused, pressed, hovered) => [styles.linkButton, styles.sideLinkButton, (focused || hovered) && styles.navFocused, pressed && styles.controlPressed]}>
                        {(focused, hovered) => <><MagnifyingGlass color={(focused || hovered || active === 'Search') ? colors.text : colors.textMuted} size={23} weight={hovered || active === 'Search' ? 'bold' : 'regular'} />{hovered && !resolvedSearchOpen ? <View pointerEvents="none" style={styles.sideHoverLabel}><Text style={styles.sideHoverLabelText}>Search</Text></View> : null}{active === 'Search' ? <View style={[styles.activeBar, styles.sideActiveBar]} /> : null}</>}
                    </FocusablePressable>
                    {railLinkList}
                    {resolvedSearchOpen ? <View style={[styles.sideSearchPopup, browseVisuals.viewport === 'television' && styles.sideSearchPopupTelevision]}><TextInput ref={setSearchInputRef} value={query} onChangeText={setQuery} onSubmitEditing={submit} onBlur={() => setSearchOpen(false)} placeholder="Search" placeholderTextColor={colors.textFaint} selectionColor={colors.brand} style={[styles.sideSearchInput, browseVisuals.viewport === 'television' && styles.sideSearchInputTelevision, Platform.OS === 'web' ? ({ outline: 'none' } as any) : undefined]} /></View> : null}
                </View> : null}
                {!compact && !sideRail ? linkList : null}
                <View style={[styles.actions, sideRail && styles.sideActions]}>
                    {searchEnabled && compact ? <FocusablePressable accessibilityLabel={resolvedSearchOpen ? 'Submit search' : 'Open search'} onPress={handleSearchPress} style={(focused, pressed) => [styles.iconButton, focused && styles.navFocused, pressed && styles.controlPressed]}>
                        <MatureSearchIcon size={24} />
                    </FocusablePressable> : null}
                    {searchEnabled && !compact && !sideRail ? <Animated.View style={[styles.desktopSearchShell, {
                        width: searchProgress.interpolate({ inputRange: [0, 1], outputRange: [36, searchWidth] }),
                        borderColor: searchProgress.interpolate({ inputRange: [0, 1], outputRange: ['rgba(255,255,255,0)', 'rgba(255,255,255,0.18)'] }),
                        backgroundColor: searchProgress.interpolate({ inputRange: [0, 1], outputRange: ['rgba(0,0,0,0)', 'rgba(9,12,18,0.78)'] }),
                    }]}>
                        <FocusablePressable accessibilityLabel={resolvedSearchOpen ? 'Focus search' : 'Open search'} onPress={handleSearchPress} style={(focused, pressed, hovered) => [styles.iconButton, (focused || hovered) && styles.navFocused, pressed && styles.controlPressed]}>
                            {(focused, hovered) => <><MatureSearchIcon size={20} />{sideRail && hovered ? <View pointerEvents="none" style={styles.sideUtilityHoverLabel}><Text style={styles.sideHoverLabelText}>Search</Text></View> : null}</>}
                        </FocusablePressable>
                        <Animated.View pointerEvents={resolvedSearchOpen ? 'auto' : 'none'} style={[styles.desktopSearchInputShell, {
                            opacity: searchProgress,
                            transform: [{ translateX: searchProgress.interpolate({ inputRange: [0, 1], outputRange: [12, 0] }) }],
                        }]}>
                            <TextInput ref={setSearchInputRef} autoFocus={persistentSearch && !isTelevisionRuntime()} value={query} onChangeText={setQuery} onSubmitEditing={submit} onKeyPress={(event) => handleSearchKey(event.nativeEvent.key)} placeholder="Search movies, shows, and live TV" placeholderTextColor={colors.textFaint} selectionColor={colors.brand} enterKeyHint="search" style={[styles.desktopSearchInput, persistentSearch && styles.desktopSearchInputPersistent]} />
                        </Animated.View>
                    </Animated.View> : null}
                    {!compact && !sideRail && user?.Name ? <Text style={[styles.userName, browseVisuals.viewport === 'television' && styles.userNameTelevision]}>{user.Name}</Text> : null}
                    <View style={styles.userMenuContainer}>
                        <FocusablePressable focusId="shell.utility.profile" focusGroup="shell.utility" focusRole="control" focusDestinations={{ up: `shell.rail.${links[links.length - 1].label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`, down: 'shell.utility.cast' }} accessibilityLabel="Open user menu" onPress={() => { setRemoteOpen(false); setMenuOpen((value) => !value); }} style={(focused, pressed, hovered) => [styles.userButton, (focused || hovered) && styles.navFocused, pressed && styles.controlPressed]}>
                            {(focused, hovered) => <>
                            <View style={[styles.avatar, !compact && styles.avatarDesktop]}>{user?.Name ? <Text style={styles.avatarText}>{user.Name.slice(0, 1).toUpperCase()}</Text> : <UserRound color={colors.text} size={17} />}</View>
                            {!compact && !sideRail ? <ChevronDown color={colors.textMuted} size={10} strokeWidth={1.5} /> : null}
                            {sideRail && hovered ? <View pointerEvents="none" style={styles.sideUtilityHoverLabel}><Text style={styles.sideHoverLabelText}>Profile</Text></View> : null}
                            </>}
                        </FocusablePressable>
                        {menuOpen ? <View style={[styles.menu, sideRail && styles.sideMenu]}>
                            <Text numberOfLines={1} style={styles.menuUser}>{user?.Name ?? 'User'}</Text>
                            <FocusablePressable onPress={() => { setMenuOpen(false); navigation.navigate('Settings'); }} style={(focused, pressed, hovered) => [styles.menuItem, hovered && styles.menuItemHovered, focused && styles.menuItemFocused, pressed && styles.controlPressed]}><Text style={styles.menuText}>Settings</Text></FocusablePressable>
                            {user?.Policy?.IsAdministrator ? <FocusablePressable onPress={() => { setMenuOpen(false); navigation.navigate('Admin'); }} style={(focused, pressed, hovered) => [styles.menuItem, hovered && styles.menuItemHovered, focused && styles.menuItemFocused, pressed && styles.controlPressed]}><Text style={styles.menuText}>Admin dashboard</Text></FocusablePressable> : null}
                            <FocusablePressable onPress={() => { setMenuOpen(false); void signOut(); }} style={(focused, pressed, hovered) => [styles.menuItem, hovered && styles.menuItemHovered, focused && styles.menuItemFocused, pressed && styles.controlPressed]}><Text style={styles.menuText}>Sign out of Jellyflix</Text></FocusablePressable>
                        </View> : null}
                    </View>
                    <RemotePlaybackControl
                        sideRail={sideRail}
                        open={remoteOpen}
                        onToggle={() => { setMenuOpen(false); setRemoteOpen((value) => !value); }}
                        onClose={() => setRemoteOpen(false)}
                    />
                </View>
            </View>
            {compact && !bottomNav ? <View style={styles.mobileLinks}>{linkList}</View> : null}
            {compact && searchEnabled ? <Animated.View pointerEvents={resolvedSearchOpen ? 'auto' : 'none'} style={[styles.mobileSearchPanel, {
                height: searchProgress.interpolate({ inputRange: [0, 1], outputRange: [0, 54] }),
                marginTop: searchProgress.interpolate({ inputRange: [0, 1], outputRange: [0, 10] }),
                opacity: searchProgress,
                transform: [{ translateY: searchProgress.interpolate({ inputRange: [0, 1], outputRange: [-8, 0] }) }],
            }]}>
                <TextInput ref={setSearchInputRef} value={query} onChangeText={setQuery} onSubmitEditing={submit} onKeyPress={(event) => handleSearchKey(event.nativeEvent.key)} placeholder="Search movies, shows, live TV..." placeholderTextColor={colors.textFaint} enterKeyHint="search" style={styles.mobileSearchInput} />
            </Animated.View> : null}
        </View>
    );

    if (appShell) return null;
    if (!bottomNav) return topBar;

    const mobileItems = [
        { label: 'Home', icon: House, selected: !searchOpen && active === 'Home', onPress: () => navigation.reset({ index: 0, routes: [{ name: 'Home' }] }) },
        { label: 'Shows', icon: Tv, selected: !searchOpen && active === 'Library' && activeItemType === 'Series', onPress: () => navigation.reset({ index: 0, routes: [{ name: 'Library', params: { itemType: 'Series' } }] }) },
        { label: 'Movies', icon: Clapperboard, selected: !searchOpen && active === 'Library' && activeItemType === 'Movie', onPress: () => navigation.reset({ index: 0, routes: [{ name: 'Library', params: { itemType: 'Movie' } }] }) },
        { label: 'Search', icon: Search, selected: active === 'Search' || searchOpen, onPress: () => setSearchOpen((value) => !value) },
        { label: 'Account', icon: UserRound, selected: !searchOpen && (active === 'Settings' || active === 'Admin'), onPress: () => navigation.navigate('Settings') },
    ];

    return (
        <>
            <View
                pointerEvents="box-none"
                style={[
                    styles.bottomBrandHeader,
                    overlay && styles.overlay,
                    solid && styles.solid,
                    { paddingHorizontal: sidePadding },
                    overlay && insets.top > 0 && { paddingTop: spacing.sm + insets.top },
                ]}
            >
                <View style={styles.bottomBrandRow}>
                    <FocusablePressable
                        accessibilityLabel="Jellyflix home"
                        onPress={() => openLink(links[0])}
                        style={(focused, pressed) => [styles.bottomBrandButton, isTelevisionRuntime() && focused && styles.navFocused, pressed && styles.controlPressed]}
                    >
                        <Text style={[typography.brand, styles.bottomBrandText]}>JELLYFLIX</Text>
                    </FocusablePressable>
                    <RemotePlaybackControl
                        open={remoteOpen}
                        onToggle={() => { setMenuOpen(false); setRemoteOpen((value) => !value); }}
                        onClose={() => setRemoteOpen(false)}
                    />
                </View>
            </View>
            {searchOpen ? <Animated.View style={[styles.bottomSearchPanel, {
                bottom: BOTTOM_NAV_BASE_HEIGHT + insets.bottom + 10,
                opacity: searchProgress,
                transform: [{ translateY: searchProgress.interpolate({ inputRange: [0, 1], outputRange: [10, 0] }) }],
            }]}>
                <MatureSearchIcon size={21} />
                <TextInput
                    ref={searchInput}
                    value={query}
                    onChangeText={setQuery}
                    onSubmitEditing={submit}
                    onKeyPress={(event) => handleSearchKey(event.nativeEvent.key)}
                    placeholder="Search movies, shows, live TV..."
                    placeholderTextColor={colors.textFaint}
                    selectionColor={colors.brand}
                    enterKeyHint="search"
                    style={styles.bottomSearchInput}
                />
                <FocusablePressable accessibilityLabel="Close search" onPress={() => setSearchOpen(false)} style={(focused, pressed) => [styles.bottomSearchClose, isTelevisionRuntime() && focused && styles.navFocused, pressed && styles.controlPressed]}>
                    <Text style={styles.bottomSearchCloseText}>Close</Text>
                </FocusablePressable>
            </Animated.View> : null}
            <View style={[styles.bottomBar, { paddingBottom: insets.bottom }]}>
                <View style={styles.bottomItems}>
                    {mobileItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <FocusablePressable key={item.label} accessibilityLabel={item.label} onPress={item.onPress} style={(focused, pressed) => [styles.bottomItem, isTelevisionRuntime() && focused && styles.bottomItemFocused, pressed && styles.controlPressed]}>
                                {(focused, hovered) => {
                                    const highlighted = item.selected || (isTelevisionRuntime() && focused) || hovered;
                                    return <>
                                        <Icon color={highlighted ? colors.brand : colors.textMuted} size={24} strokeWidth={highlighted ? 2.3 : 1.8} />
                                        <Text style={[styles.bottomLabel, highlighted && styles.bottomLabelActive]}>{item.label}</Text>
                                        {item.selected ? <View style={styles.bottomActiveDot} /> : null}
                                    </>;
                                }}
                            </FocusablePressable>
                        );
                    })}
                </View>
            </View>
        </>
    );
}

const styles = StyleSheet.create({
    root: { backgroundColor: 'rgba(0,0,0,0.88)', paddingTop: spacing.sm, paddingBottom: spacing.sm, zIndex: 20 },
    rootSideRail: { flex: 1, overflow: 'visible', backgroundColor: 'transparent', paddingTop: 24, paddingBottom: 18, zIndex: 20 },
    rootDesktop: { paddingTop: 10, paddingBottom: 10 },
    rootTelevision: { paddingTop: 15, paddingBottom: 15 },
    bottomBrandHeader: { minHeight: 48, paddingTop: spacing.sm, paddingBottom: spacing.xs, justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.88)', zIndex: 20 },
    bottomBrandRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    bottomBrandButton: { alignSelf: 'flex-start', paddingVertical: spacing.xs, borderWidth: 1, borderColor: 'transparent' },
    bottomBrandText: { fontSize: 18, lineHeight: 22 },
    overlay: { position: 'absolute', top: 0, left: 0, right: 0, backgroundColor: 'transparent' },
    solid: { backgroundColor: 'rgba(0,0,0,0.96)' },
    bottomBar: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        elevation: 30,
        minHeight: BOTTOM_NAV_BASE_HEIGHT,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: 'rgba(255,255,255,0.10)',
        backgroundColor: 'rgba(5,7,10,0.97)',
    },
    bottomItems: { minHeight: BOTTOM_NAV_BASE_HEIGHT, flexDirection: 'row', alignItems: 'stretch' },
    bottomItem: { flex: 1, minWidth: 0, alignItems: 'center', justifyContent: 'center', gap: 3, paddingTop: 7, paddingBottom: 8, borderRadius: 8 },
    bottomItemFocused: { backgroundColor: 'rgba(255,255,255,0.06)' },
    bottomLabel: { color: colors.textMuted, fontFamily: systemSansFont, fontSize: 11, lineHeight: 14, fontWeight: '500' },
    bottomLabelActive: { color: colors.brand, fontWeight: '700' },
    bottomActiveDot: { width: 4, height: 4, marginTop: 1, borderRadius: 2, backgroundColor: colors.brand },
    bottomSearchPanel: {
        position: 'absolute',
        left: 12,
        right: 12,
        zIndex: 31,
        elevation: 31,
        minHeight: 52,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 14,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.18)',
        borderRadius: 12,
        backgroundColor: 'rgba(9,12,18,0.98)',
        shadowColor: '#000',
        shadowOpacity: 0.38,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 5 },
    },
    bottomSearchInput: { flex: 1, minWidth: 0, paddingVertical: 12, color: colors.text, fontFamily: systemSansFont, fontSize: 15 },
    bottomSearchClose: { minHeight: 34, justifyContent: 'center', paddingHorizontal: 7, borderRadius: 6 },
    bottomSearchCloseText: { color: colors.textMuted, fontFamily: systemSansFont, fontSize: 12, fontWeight: '600' },
    navGradient: { position: 'absolute', top: 0, left: 0 },
    mainRow: { minHeight: 36, flexDirection: 'row', alignItems: 'center', gap: 44 },
    mainRowDesktop: { minHeight: 32 },
    sideMainRow: { position: 'absolute', top: 0, bottom: 0, left: 12, right: 12, flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', gap: 16 },
    sidePrimaryGroup: { position: 'absolute', top: '50%', left: 0, width: 32, alignSelf: 'center', alignItems: 'center', justifyContent: 'center', gap: 10, zIndex: 100, elevation: 100, transform: [{ translateY: -121 }] },
    railLinkList: { alignItems: 'center', gap: 10, overflow: 'visible', zIndex: 110, elevation: 110 },
    actions: { flexDirection: 'row', alignItems: 'center', gap: spacing.lg, marginLeft: 'auto' },
    sideActions: { width: 32, marginLeft: 0, marginTop: 'auto', marginBottom: 0, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 },
    brandButton: { paddingVertical: spacing.xs, borderWidth: 1, borderColor: 'transparent' },
    brandButtonCompact: { marginLeft: -4 },
    iconButton: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
    userMenuContainer: { position: 'relative' },
    userButton: { minHeight: 36, padding: 3, flexDirection: 'row', alignItems: 'center', gap: spacing.sm, borderWidth: 1, borderColor: 'transparent', borderRadius: 5 },
    avatar: { width: 28, height: 28, borderRadius: 4, backgroundColor: colors.brand, alignItems: 'center', justifyContent: 'center' },
    avatarDesktop: { width: 32, height: 32 },
    brandDesktop: { fontSize: 20 },
    brandTelevision: { fontSize: 24, lineHeight: 29 },
    avatarText: { color: colors.text, fontFamily: systemSansFont, fontSize: 14, fontWeight: '800' },
    userName: { maxWidth: 140, color: colors.text, fontFamily: systemSansFont, fontSize: 14, fontWeight: '600' },
    userNameTelevision: { fontSize: 17, lineHeight: 22 },
    menu: { position: 'absolute', top: 42, right: 0, width: 220, paddingVertical: spacing.sm, borderRadius: 6, borderWidth: 1, borderColor: colors.border, backgroundColor: '#171717', shadowColor: '#000', shadowOpacity: 0.5, shadowRadius: 16, elevation: 18 },
    sideMenu: { top: -152, bottom: undefined, left: 42, right: undefined },
    menuUser: { color: colors.textMuted, fontSize: 12, fontWeight: '700', paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
    menuItem: { minHeight: 42, paddingHorizontal: spacing.lg, justifyContent: 'center', borderLeftWidth: 3, borderLeftColor: 'transparent' },
    menuItemHovered: { backgroundColor: 'rgba(255,255,255,0.08)' },
    menuItemFocused: { backgroundColor: 'rgba(255,255,255,0.08)', borderWidth: 3, borderColor: 'rgba(255,255,255,0.92)', shadowColor: colors.brand, shadowOpacity: 0.72, shadowRadius: 7, elevation: 8 },
    menuText: { color: colors.text, fontSize: 13, fontWeight: '600' },
    desktopLinks: { flexGrow: 0, flexShrink: 1 },
    sideRailLinks: { overflow: 'visible', zIndex: 80, elevation: 80 },
    sideLinkButton: { width: 32, minHeight: 32, paddingHorizontal: 0, justifyContent: 'center', alignItems: 'center', alignSelf: 'center', overflow: 'visible', zIndex: 110, elevation: 110 },
    sideRailLinkText: { display: 'none' },
    sideRailExpandedLinkText: { marginTop: 4, fontSize: 11, lineHeight: 14, textAlign: 'center' },
    sideBrand: { width: 154, fontSize: 18, lineHeight: 22, flexShrink: 0 },
    sideBrandButton: { position: 'absolute', top: 0, left: 10, width: 160, height: 28, alignItems: 'flex-start', justifyContent: 'center', paddingHorizontal: 4, overflow: 'visible' },
    desktopLinksContent: { gap: 10 },
    mobileLinks: { marginTop: spacing.md, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.border, paddingTop: spacing.sm },
    mobileLinksContent: { flexGrow: 1, justifyContent: 'space-evenly', alignItems: 'center', gap: 4 },
    linkButton: { position: 'relative', minHeight: 31, justifyContent: 'center', paddingHorizontal: 7 },
    linkButtonCompact: { minHeight: 0, paddingHorizontal: 4, paddingVertical: 5, alignItems: 'center', flexShrink: 0 },
    navFocused: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)', borderRadius: 5, backgroundColor: 'rgba(255,255,255,0.08)' },
    linkText: { color: 'rgba(255,255,255,0.68)', fontFamily: systemSansFont, fontSize: 14, lineHeight: 17, fontWeight: '500' },
    linkTextCompact: { textAlign: 'center', fontSize: 12, lineHeight: 18 },
    linkTextHovered: { color: colors.text },
    linkTextActive: { color: colors.text, fontWeight: '700' },
    activeBar: { position: 'absolute', left: 7, right: 7, bottom: 0, height: 2, borderRadius: 2, backgroundColor: colors.brand },
    activeBarCompact: { bottom: 0 },
    sideActiveBar: { left: '50%', right: undefined, width: 22, transform: [{ translateX: -11 }] },
    sideHoverLabel: { position: 'absolute', left: 38, top: 1, zIndex: 120, elevation: 120, alignSelf: 'flex-start', minHeight: 30, justifyContent: 'center', paddingHorizontal: 10, borderRadius: 5, backgroundColor: 'rgba(12,14,18,0.96)', shadowColor: '#000', shadowOpacity: 0.35, shadowRadius: 8 },
    sideHoverLabelText: { color: colors.text, fontFamily: systemSansFont, fontSize: 13, lineHeight: 16, fontWeight: '600', flexShrink: 0 },
    sideUtilityHoverLabel: { position: 'absolute', left: 38, top: 1, minHeight: 30, justifyContent: 'center', paddingHorizontal: 10, borderRadius: 5, backgroundColor: 'rgba(12,14,18,0.92)', shadowColor: '#000', shadowOpacity: 0.35, shadowRadius: 8, elevation: 8 },
    controlPressed: { opacity: 0.7, transform: [{ scale: 0.97 }] },
    desktopSearchShell: { height: 36, minWidth: 36, flexDirection: 'row', alignItems: 'center', overflow: 'hidden', borderWidth: 1, borderRadius: 5 },
    sideSearchPopup: { position: 'absolute', left: 38, top: -3, width: 250, height: 38, zIndex: 70, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)', borderRadius: 5, backgroundColor: 'rgba(11,14,19,0.96)' },
    sideSearchPopupTelevision: { left: 44, top: -8, width: 330, height: 52, borderWidth: 2, borderColor: 'rgba(255,255,255,0.42)', borderRadius: 8, backgroundColor: '#111722', shadowColor: '#000', shadowOpacity: 0.72, shadowRadius: 18, elevation: 18 },
    sideSearchInput: { flex: 1, paddingHorizontal: 10, color: colors.text, fontFamily: systemSansFont, fontSize: 14 },
    sideSearchInputTelevision: { paddingHorizontal: 16, fontSize: 18 },
    desktopSearchInputShell: { flex: 1, height: 34, justifyContent: 'center', marginLeft: 2, marginRight: 8, position: 'relative', overflow: 'hidden', borderRadius: 4 },
    desktopSearchInput: { position: 'absolute', top: -4, right: -4, bottom: -4, left: -4, minWidth: 0, paddingVertical: 0, paddingHorizontal: spacing.md, borderWidth: 0, backgroundColor: 'transparent', color: colors.textMuted, fontFamily: systemSansFont, fontSize: 14 },
    desktopSearchInputPersistent: { color: colors.text, fontSize: 15 },
    mobileSearchPanel: { overflow: 'hidden', justifyContent: 'center' },
    mobileSearchInput: { height: 44, paddingHorizontal: spacing.lg, borderWidth: 1, borderColor: colors.brandBorder, borderRadius: 6, backgroundColor: colors.surfaceRaised, color: colors.text, fontSize: 16 },
});
